import { useEffect, useState, useCallback } from "react";
import { Crown, Soup } from "lucide-react";
import { getBoard, getResults } from "../lib/api";
import { Reveal } from "./Reveal";
import { winnerPhotoUrl } from "../lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const TastingBallot = ({ party }) => {
  const [soups, setSoups] = useState([]);
  const [results, setResults] = useState({ results: [], total: 0 });

  const refresh = useCallback(() => {
    getBoard().then((b) => setSoups(b.soups)).catch(() => {});
    getResults().then(setResults).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000);
    const onUpdate = () => refresh();
    window.addEventListener("rsvp-updated", onUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener("rsvp-updated", onUpdate);
    };
  }, [refresh]);

  const counts = Object.fromEntries(results.results.map((r) => [r.soup, r.count]));
  const max = Math.max(1, ...results.results.map((r) => r.count));
  const leader = results.results[0]?.soup;
  const uniqueSoups = soups.filter((s, i, arr) => arr.findIndex((x) => x.soup.toLowerCase() === s.soup.toLowerCase()) === i);

  return (
    <section id="ballot" className="relative bg-[#C88F52] px-6 md:px-16 lg:px-24 py-24 md:py-36" data-testid="ballot-section">
      <Reveal x={80}>
        <p className="font-body text-[11px] uppercase tracking-[0.35em] text-harvest mb-4">During the Party</p>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream">
          The Tasting Ballot — <span className="font-accent text-[#F2E7D5] text-4xl md:text-6xl align-middle -rotate-2 inline-block">crown a crowd favorite!</span>
        </h2>
        <p className="mt-5 font-body text-base md:text-lg text-cream/85 leading-relaxed max-w-2xl">
          Yes, there's a tasting contest. Sample your way down the table, then scan the QR code beside your favorite pot to vote for it right from your phone — one vote per person, and you can change your mind until the ladles are packed away. Results update live below all evening.
        </p>
      </Reveal>

      {party?.winner_revealed && results.total > 0 && leader && (
        <Reveal x={-80} className="mt-12">
          <div className="border-2 border-harvest bg-moss px-8 md:px-12 py-10 text-center shadow-[8px_8px_0_0_#2E4028]" data-testid="winner-banner">
            {party?.winner_photo_path && (
              <img
                src={winnerPhotoUrl(party.winner_photo_path)}
                alt={`${leader} — the winning soup`}
                className="mx-auto mb-6 h-44 w-44 object-cover border-4 border-[#FBF4E6] shadow-xl rotate-2"
                data-testid="winner-photo"
              />
            )}
            <Crown className="mx-auto text-harvest mb-4" size={36} />
            <p className="font-accent text-3xl text-ember">the golden ladle goes to</p>
            <p className="font-display italic text-4xl md:text-6xl text-cream mt-2">{leader}</p>
            <p className="font-body text-xs uppercase tracking-[0.3em] text-fog mt-3">
              {counts[leader]} vote{counts[leader] === 1 ? "" : "s"} — crowd favorite
            </p>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.1} className="mt-14 md:mt-20">
        <div className="notice-block p-8 md:p-12 max-w-4xl" data-testid="ballot-results">
          <div className="flex items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Crown className="text-harvest" size={24} />
              <h3 className="font-display text-2xl md:text-3xl text-cream">Live Results</h3>
            </div>
            <p className="font-body text-xs text-fog/70 uppercase tracking-[0.2em] flex items-center gap-2">
              <Soup size={14} className="text-ember" />
              {results.total} vote{results.total === 1 ? "" : "s"} cast
            </p>
          </div>
          {soups.length === 0 ? (
            <p className="font-accent text-3xl text-fog -rotate-1" data-testid="ballot-empty">the ballot opens once the first soup is claimed</p>
          ) : (
            <ul className="space-y-5">
              {uniqueSoups.map((entry) => {
                const count = counts[entry.soup] || 0;
                const isLeader = results.total > 0 && entry.soup === leader;
                return (
                  <li key={entry.soup} data-testid={`ballot-row-${slug(entry.soup)}`}>
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <span className="font-display italic text-2xl md:text-3xl text-cream leading-snug flex items-center gap-2">
                        {entry.soup}
                        {isLeader && <Crown size={20} className="text-harvest shrink-0" />}
                      </span>
                      <span className="font-body text-xs uppercase tracking-[0.2em] text-fog shrink-0">
                        {count} vote{count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="h-2 bg-forest/10 overflow-hidden">
                      <div
                        className={`h-full transition-[width] duration-700 ${isLeader ? "bg-harvest" : "bg-ember"}`}
                        style={{ width: `${(count / max) * 100}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Reveal>
    </section>
  );
};

